import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import { apiClient } from '../services/api';
import { getCurrentUser } from '../services/auth';
import { SUPPORTED_AUDIO_FORMATS, SUPPORTED_VIDEO_FORMATS } from '../types';
import './Upload.css';

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    // Fetch remaining minutes
    const fetchUsage = async () => {
      try {
        const usage = await apiClient.getUsage();
        const remaining = usage.transcribeMinutesLimit - usage.transcribeMinutesUsed;
        setRemainingMinutes(Math.max(0, remaining));
      } catch (err: any) {
        console.error('Failed to fetch usage:', err);
      }
    };
    fetchUsage();
  }, []);

  const validateFile = (file: File): string | null => {
    // Check file format
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension) {
      return 'Invalid file format';
    }

    const isAudio = SUPPORTED_AUDIO_FORMATS.includes(extension);
    const isVideo = SUPPORTED_VIDEO_FORMATS.includes(extension);

    if (!isAudio && !isVideo) {
      return `Unsupported file format. Supported formats: ${[...SUPPORTED_AUDIO_FORMATS, ...SUPPORTED_VIDEO_FORMATS].join(', ')}`;
    }

    return null;
  };

  const getMediaDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const isVideo = SUPPORTED_VIDEO_FORMATS.includes(extension || '');
      
      const mediaElement = isVideo 
        ? document.createElement('video')
        : document.createElement('audio');
      
      const objectUrl = URL.createObjectURL(file);
      
      mediaElement.preload = 'metadata';
      
      mediaElement.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        const durationSeconds = Math.ceil(mediaElement.duration);
        
        if (isNaN(durationSeconds) || durationSeconds === 0) {
          reject(new Error('Could not determine media duration'));
        } else {
          resolve(durationSeconds);
        }
      };
      
      mediaElement.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load media file'));
      };
      
      mediaElement.src = objectUrl;
    });
  };

  const handleFileSelect = async (selectedFile: File) => {
    setError(null);
    setSuccess(null);
    setDuration(null);

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFile(selectedFile);
    
    // Extract duration from media file
    try {
      const durationSeconds = await getMediaDuration(selectedFile);
      setDuration(durationSeconds);
      
    } catch (err) {
      console.error('Failed to get media duration:', err);
      // Don't block upload if duration extraction fails
      // Backend will use default 10 minutes
      setDuration(null);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const user = getCurrentUser();
      if (!user) {
        setError('User not authenticated');
        setUploading(false);
        return;
      }

      // Step 1: Request upload URL
      const uploadResponse = await apiClient.requestUpload({
        userId: user.userId,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
        duration: duration || undefined, // Include duration if available
      });

      // Step 2: Upload file to S3
      await apiClient.uploadToS3(uploadResponse.uploadUrl, file);

      // Success
      setSuccess(`File uploaded successfully! Job ID: ${uploadResponse.jobId}`);
      setFile(null);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      console.error('Upload error:', err);
      
      // Handle specific error cases
      if (err.response?.status === 403) {
        setError(err.response.data?.message || 'Your account is pending approval. Please contact an administrator.');
      } else if (err.response?.status === 429) {
        const message = err.response.data?.message || 'Upload limit reached';
        const queuePosition = err.response.data?.queuePosition;
        if (queuePosition) {
          setError(`${message}. Your job is queued at position ${queuePosition}`);
        } else {
          setError(message);
        }
      } else {
        setError(err.response?.data?.message || 'Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-page">
      <div className="container">
        <h1>Upload Media File</h1>
        
        {remainingMinutes !== null && (
          <div className="usage-info">
            <p>Remaining transcription minutes this month: <strong>{remainingMinutes.toFixed(1)}</strong></p>
          </div>
        )}

        <Card>
          <h2>Upload Your Swahili Content</h2>
          <p className="text-secondary">
            Upload audio (MP3, WAV, FLAC, M4A) or video (MP4, AVI, MOV, MKV) files.
            Supported formats: MP3, WAV, FLAC, M4A, MP4, AVI, MOV, MKV.
          </p>
          
          <div
            className={`upload-dropzone ${dragActive ? 'active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-input"
              accept={[...SUPPORTED_AUDIO_FORMATS.map(f => `.${f}`), ...SUPPORTED_VIDEO_FORMATS.map(f => `.${f}`)].join(',')}
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input" className="upload-label">
              {file ? (
                <div className="file-selected">
                  <p><strong>{file.name}</strong></p>
                  <p className="text-secondary">
                    Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                    {duration && ` • Duration: ${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`}
                  </p>
                </div>
              ) : (
                <div className="upload-prompt">
                  <p>Drag and drop your file here, or click to browse</p>
                </div>
              )}
            </label>
          </div>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              {success}
            </div>
          )}

          <div className="upload-actions">
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
            {file && !uploading && (
              <Button
                variant="secondary"
                onClick={() => {
                  setFile(null);
                  setDuration(null);
                  setError(null);
                  setSuccess(null);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Upload;
